import { Check } from 'lucide-react';

import { MIA } from '@/components/landing/v3/mapaSeoDatos';
import { rankColor, rankLabel } from '@/lib/geogridScale';

/**
 * ComoFuncionaV3 — los tres pasos de la home, cada uno con su tarjeta-maqueta.
 *
 * Las tres maquetas son JSX y CSS, no capturas: el diseño las dibuja así y es
 * lo correcto —pesan cero, escalan sin pixelarse y no hay que mantener una
 * imagen sincronizada con un producto que cambia—. La única que necesitaría un
 * fichero es el mini mapa, y se resuelve reutilizando la misma rejilla de datos
 * que el comparador de más abajo.
 *
 * Las tres van `aria-hidden`: son ilustraciones de lo que el título y el
 * párrafo de debajo ya cuentan. Leer «1.218 respondidas / 85 /100 / Tu media
 * 4,7» en voz alta, sin contexto, no aporta nada.
 */
export default function ComoFuncionaV3({ c }) {
  const maquetas = { conectar: <MockFicha c={c.mockFicha} />, mapa: <MockMapa c={c.mockMapa} />, analisis: <MockAnalisis c={c.mockAnalisis} /> };

  return (
    <section id="como-funciona" className="bg-white px-6 pt-6 pb-12">
      <div className="max-w-[1180px] mx-auto bg-[#f6f5f3] rounded-[28px] px-6 py-12 md:px-10 md:py-14">
        <h2 className="text-[28px] md:text-[34px] leading-[1.15] tracking-[-.025em] font-bold text-center text-v3-ink">
          {c.titulo}
        </h2>
        <p className="text-[16.5px] leading-[1.6] text-v3-body text-center mt-3 mb-11 mx-auto max-w-[56ch]">
          {c.subtitulo}
        </p>

        <div className="grid gap-8 md:grid-cols-3">
          {c.pasos.map((paso, i) => (
            <div key={paso.clave} className="flex flex-col">
              <div
                aria-hidden="true"
                className="h-[264px] flex items-end justify-center px-1 pb-2"
              >
                {maquetas[paso.clave]}
              </div>

              {/* El número del paso, con su línea a los lados. La del primero no
                  sale por la izquierda ni la del último por la derecha: la línea
                  une pasos, y fuera de la serie no une nada. */}
              <div className="flex items-center mt-4.5" style={{ marginTop: 18 }}>
                <span
                  className={`flex-1 h-px ${i === 0 ? 'bg-transparent' : 'bg-v3-line'}`}
                  aria-hidden="true"
                />
                <span className="w-[34px] h-[34px] rounded-full bg-v3-gold text-v3-ink text-sm font-bold flex items-center justify-center shrink-0">
                  {i + 1}
                </span>
                <span
                  className={`flex-1 h-px ${i === c.pasos.length - 1 ? 'bg-transparent' : 'bg-v3-line'}`}
                  aria-hidden="true"
                />
              </div>

              <div className="text-center px-2">
                <div className="text-[20px] font-bold tracking-[-.02em] mt-4.5 leading-[1.25] text-v3-ink" style={{ marginTop: 18 }}>
                  {paso.titulo}
                </div>
                <div className="text-[15px] leading-[1.6] text-v3-body mt-2">{paso.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Paso 1: la ficha de Google recién conectada. */
function MockFicha({ c }) {
  return (
    <div className="bg-white rounded-[18px] shadow-[0_10px_30px_rgba(0,0,0,.09)] p-4.5 w-full max-w-[300px] h-[236px] relative flex flex-col justify-between" style={{ padding: 18 }}>
      <div className="flex items-center gap-3">
        <div className="w-[42px] h-[42px] rounded-xl bg-[#eef3fd] flex items-center justify-center shrink-0">
          <IconoGoogle />
        </div>
        <div className="min-w-0">
          <div className="text-[14.5px] font-bold tracking-[-.01em] text-v3-ink">{c.negocio}</div>
          <div className="text-[12.5px] text-v3-muted mt-px">
            4,7 <span className="text-v3-gold tracking-[.5px]">★★★★★</span> · {c.fuente}
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="flex-1 bg-v3-surface rounded-xl px-3 py-2.5">
          <div className="text-[10.5px] text-v3-muted">{c.resenas}</div>
          <div className="flex items-baseline gap-1.5 mt-px">
            <span className="text-base font-bold tracking-[-.02em] text-v3-ink">1.218</span>
            <span className="text-[10.5px] font-semibold text-v3-green">{c.respondidas}</span>
          </div>
        </div>
        <div className="flex-1 bg-v3-surface rounded-xl px-3 py-2.5">
          <div className="text-[10.5px] text-v3-muted">{c.puntuacion}</div>
          <div className="flex items-baseline gap-0.5 mt-px">
            <span className="text-base font-bold tracking-[-.02em] text-v3-ink">85</span>
            <span className="text-[10.5px] text-v3-faint">/100</span>
          </div>
        </div>
      </div>

      <div className="bg-v3-goldsoft rounded-xl px-3.5 py-3">
        <div className="flex items-center justify-between gap-2.5">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[10.5px] font-semibold text-v3-golddeep">{c.tuMedia}</span>
            <span className="text-[15px] font-bold tracking-[-.02em] text-v3-ink">4,7</span>
            <span className="text-[11px] text-v3-gold">★</span>
          </div>
          <div className="text-[10.5px] text-v3-body">
            {c.objetivo} <span className="font-bold text-v3-ink">4,8</span>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="flex-1 h-1.5 rounded-full bg-white/75 overflow-hidden">
            <div className="h-full w-[84%] rounded-full bg-v3-golddeep" />
          </div>
          <span className="text-[10.5px] font-bold text-v3-golddeep">84%</span>
        </div>
        <div className="text-[10.5px] text-v3-body mt-1.5">
          {c.faltanA}
          <span className="font-bold text-v3-ink">{c.faltanResenas}</span>
          {c.faltanB}
        </div>
      </div>

      <div className="absolute -right-2 -bottom-3.5 flex items-center gap-1.5 bg-v3-green text-white rounded-full px-3 py-2 shadow-[0_4px_14px_rgba(22,163,74,.3)]">
        <Check className="w-3 h-3" strokeWidth={3} />
        <span className="text-[11.5px] font-semibold whitespace-nowrap">{c.conectada}</span>
      </div>
    </div>
  );
}

/** Paso 2: el mini mapa 5x5. Reutiliza la rejilla del comparador de abajo. */
function MockMapa({ c }) {
  return (
    <div className="bg-white rounded-[18px] shadow-[0_10px_30px_rgba(0,0,0,.09)] p-3.5 w-full max-w-[300px] h-[236px] flex flex-col">
      <div className="flex items-center justify-between gap-2.5 px-0.5 pb-3">
        <span className="text-[13px] font-bold tracking-[-.01em] text-v3-ink">{c.titulo}</span>
      </div>

      <div className="relative rounded-xl overflow-hidden bg-[#f2efe9] flex-1 min-h-0 flex items-center justify-center">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-0 right-0 top-[24%] h-2 bg-white" />
          <div className="absolute left-0 right-0 top-[64%] h-[11px] bg-[#fdf0d0] border-y border-[#f2e2b8]" />
          <div className="absolute top-0 bottom-0 left-[22%] w-2 bg-white" />
          <div className="absolute top-0 bottom-0 left-[63%] w-[11px] bg-[#fdf0d0] border-x border-[#f2e2b8]" />
          <div className="absolute left-0 top-0 w-[18%] h-[20%] bg-[#d8e8d0]" />
          <div className="absolute right-0 bottom-0 w-[22%] h-[24%] bg-[#cfe3ef]" />
          <div className="absolute left-[28%] top-[30%] w-[28%] h-[30%] bg-[#eae7e0]" />
        </div>

        <div className="relative grid grid-cols-5 gap-[9px] h-[86%] aspect-square">
          {/* Mismos colores y etiquetas que el panel (`geogridScale`), y sin
              el pin negro del centro: `GeoGridMap` no dibuja ninguno, el
              centro es un punto medido como los demás. */}
          {MIA.map((n, i) => {
            const puesto = n > 0 ? n : null;
            return (
              <div
                key={i}
                className="aspect-square rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                style={{ background: rankColor(puesto), opacity: 0.92, border: '2px solid #ffffff' }}
              >
                {rankLabel(puesto)}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2.5 px-0.5 pt-3">
        <span className="text-[11.5px] text-v3-muted">{c.posicionMedia}</span>
        <span className="text-[15px] font-bold tracking-[-.02em] text-v3-green">2,4</span>
      </div>
    </div>
  );
}

/** Paso 3: la puntuación con su próxima acción. */
function MockAnalisis({ c }) {
  const bloques = [
    { pct: 90, color: '#16a34a' },
    { pct: 100, color: '#16a34a' },
    { pct: 85, color: '#16a34a' },
    { pct: 75, color: '#feae2c' },
    { pct: 70, color: '#feae2c' },
    { pct: 39, color: '#dc2626' },
  ];

  return (
    <div className="bg-white rounded-[18px] shadow-[0_10px_30px_rgba(0,0,0,.09)] w-full max-w-[300px] h-[236px] flex flex-col justify-between" style={{ padding: 18 }}>
      <div className="flex items-center gap-3.5">
        <div
          className="w-[62px] h-[62px] rounded-full flex items-center justify-center shrink-0"
          style={{ background: 'conic-gradient(#feae2c 0 78%, #ececea 78% 100%)' }}
        >
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-[19px] font-bold tracking-[-.02em] text-v3-ink">
            78
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold text-v3-faint">{c.nivel}</div>
          <div className="text-base font-bold tracking-[-.02em] mt-px text-v3-ink">{c.etiqueta}</div>
          <div className="text-xs text-v3-green font-semibold mt-0.5">{c.progreso}</div>
        </div>
      </div>

      <div className="bg-v3-surface rounded-xl px-3.5 py-3">
        <div className="text-[10.5px] font-semibold text-v3-golddeep">{c.proxima}</div>
        <div className="text-[13.5px] font-semibold tracking-[-.01em] mt-1.5 text-v3-ink">
          {c.accion}
        </div>
        <div className="inline-block text-[11px] font-semibold text-v3-green bg-v3-greensoft rounded-full px-2.5 py-1 mt-2.5">
          {c.puntos}
        </div>
      </div>

      <div className="flex gap-1.5">
        {bloques.map((b) => (
          <div key={b.pct + b.color} className="flex-1 h-1.5 rounded-full bg-v3-track overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${b.pct}%`, background: b.color }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * El icono de Google Business.
 *
 * En el diseño era `uploads/pasted-1789461479578-0.png`: un PNG de 512×512 para
 * pintarse a 32 px. Como SVG se ve nítido, no hay que bajar nada y son los
 * cuatro colores de la marca, que es lo único que se reconoce a ese tamaño.
 */
function IconoGoogle() {
  return (
    <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-2.8-.4-4H24v7.3h12.1c-.2 2-1.6 5-4.5 7l-.1.3 6.5 5 .5.1c4.1-3.8 6.6-9.4 6.6-15.7z" />
      <path fill="#34A853" d="M24 46c5.9 0 10.9-1.9 14.5-5.3l-6.9-5.3c-1.8 1.3-4.3 2.2-7.6 2.2-5.8 0-10.7-3.8-12.5-9.1l-.3
        .1-6.7 5.2-.1.3C7.9 41 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.5 28.5c-.5-1.4-.7-2.9-.7-4.5s.3-3.1.7-4.5v-.3l-6.8-5.3-.2.1C2.9 17.1 2 20.4 2 24s.9 6.9 2.5 9.9l7-5.4z" />
      <path fill="#EA4335" d="M24 10.6c4.1 0 6.9 1.8 8.5 3.3l6.2-6C34.9 4.4 29.9 2 24 2 15.4 2 7.9 7 4.5 14.1l7 5.4c1.8-5.3 6.7-8.9 12.5-8.9z" />
    </svg>
  );
}
